import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tree, Waves, ArrowRight, X } from "@phosphor-icons/react";
import { FoundingBadge, getTierConfig } from "@/components/dashboard/FoundingBadge";

const STORAGE_KEY = "eviq_welcome_seen_v1";

export const WelcomeExperience = ({ user }) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (!user) return;
        const key = `${STORAGE_KEY}_${user.user_id}`;
        const seen = typeof window !== "undefined" && window.localStorage.getItem(key);
        if (!seen) {
            const t = setTimeout(() => setOpen(true), 400);
            return () => clearTimeout(t);
        }
    }, [user]);

    if (!user) return null;
    const cfg = getTierConfig(user.founder_tier);

    const close = () => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(`${STORAGE_KEY}_${user.user_id}`, "1");
        }
        setOpen(false);
    };

    const steps = [
        {
            eyebrow: "Welcome to EVIQ Earth Initiative",
            title: (
                <>
                    Hello, <span className="italic text-zinc-300">{user.display_name.split(" ")[0]}</span>.
                    <br />
                    You're already changing the world.
                </>
            ),
            body: "Every EVIQ membership quietly funds forest restoration, ocean cleanup, battery recycling, veteran clean-energy careers, and community charging. No donation prompts. Just outcomes.",
            cta: "See what I've done",
            visual: (
                <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-4 text-center">
                        <Tree size={22} color="#a3e635" weight="fill" className="mx-auto" />
                        <div className="mt-3 font-mono-metric text-3xl font-bold text-white">
                            {user.trees_restored}
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">Trees restored</div>
                    </div>
                    <div className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-4 text-center">
                        <Waves size={22} color="#06b6d4" weight="fill" className="mx-auto" />
                        <div className="mt-3 font-mono-metric text-3xl font-bold text-white">
                            {user.ocean_plastic_lbs}
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">Lbs ocean plastic</div>
                    </div>
                    <div className="col-span-2 rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-4">
                        <div className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            Signup impact
                        </div>
                        <p className="mt-2 text-sm text-zinc-200">
                            EVIQ planted <span className="text-[#a3e635]">1 tree</span> the moment
                            you joined. Every milestone plants more.
                        </p>
                    </div>
                </div>
            ),
        },
        {
            eyebrow: cfg.cap,
            title: (
                <>
                    You're one of the
                    <br />
                    <span className="italic" style={{ color: cfg.color }}>
                        {cfg.cap.toLowerCase()}.
                    </span>
                </>
            ),
            body: cfg.celebratory + " Your Founding badge will follow you across EVIQ — visible in your profile, on shared reports, and in the community.",
            cta: "Enter my dashboard",
            visual: (
                <div className="flex flex-col items-center gap-6">
                    <FoundingBadge
                        tier={user.founder_tier}
                        memberNumber={user.member_number}
                        size="lg"
                        testId="welcome-founding-badge"
                    />
                    <div className="text-center">
                        <div
                            className="font-editorial text-2xl"
                            style={{ color: cfg.color }}
                        >
                            {cfg.label}
                        </div>
                        <p className="mt-1 font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            Permanent · Numbered · Non-transferable
                        </p>
                    </div>
                </div>
            ),
        },
    ];

    const current = steps[step];

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                    data-testid="welcome-modal"
                >
                    <div
                        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
                        onClick={close}
                        aria-hidden
                    />
                    <motion.div
                        initial={{ y: 20, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 20, opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative z-10 grid max-h-[92vh] w-full max-w-4xl grid-cols-1 overflow-y-auto rounded-lg border border-[#27272a] bg-[#080808] lg:max-h-none lg:grid-cols-[1.05fr_1fr]"
                    >
                        <button
                            type="button"
                            onClick={close}
                            aria-label="Close welcome"
                            data-testid="welcome-close"
                            className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-[#27272a] bg-black/40 text-zinc-400 transition-colors duration-200 hover:text-white"
                        >
                            <X size={14} />
                        </button>

                        <div className="relative p-6 sm:p-8 lg:p-12">
                            <span
                                className="font-mono-metric text-[10px] uppercase tracking-[0.28em]"
                                style={{ color: cfg.color }}
                            >
                                {current.eyebrow}
                            </span>
                            <motion.h2
                                key={step}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45 }}
                                className="mt-4 font-editorial text-2xl leading-tight text-white sm:text-3xl md:text-4xl"
                            >
                                {current.title}
                            </motion.h2>
                            <p className="mt-5 max-w-md text-sm leading-relaxed text-zinc-400">
                                {current.body}
                            </p>

                            <div className="mt-8 flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => (step === 0 ? setStep(1) : close())}
                                    data-testid={
                                        step === 0 ? "welcome-next-btn" : "welcome-enter-btn"
                                    }
                                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                                    style={{ background: cfg.color }}
                                >
                                    {current.cta}
                                    <ArrowRight size={14} weight="bold" />
                                </button>
                                <div className="flex items-center gap-1.5">
                                    {steps.map((_, i) => (
                                        <span
                                            key={i}
                                            className="h-1 w-6 rounded-full transition-colors duration-200"
                                            style={{
                                                background:
                                                    i === step ? cfg.color : "#1f1f22",
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="relative border-t border-[#1f1f22] bg-gradient-to-br from-[#0a0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:border-l lg:border-t-0 lg:p-12">
                            <motion.div
                                key={`v-${step}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                {current.visual}
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default WelcomeExperience;
